import React, { useCallback, useEffect, useState } from 'react';
import DownloadExcel from '../../assets/download-excel.png';

import { useForm, Controller, FormProvider } from 'react-hook-form';

import {
  IDynamicFilterData,
  IDynamicFilterSelectOptions,
} from '../../interfaces';
import { Input } from '../Input';
import * as DF from '../../styles/lib/dynamic-filter';

import { InputDate } from '../InputDate';
import { masks } from '../../constants/masks';
import { formatCNPJ, formatCPF, onlyNumbers } from '../../utils/functions';

interface DynamicFilterProps {
  filterSelectOptions: IDynamicFilterSelectOptions[];
  filters: IDynamicFilterData;
  extraFilterSelectOptions?: IDynamicFilterSelectOptions[];
  calendar?: boolean;
  isLoading: boolean;
  dataTreatment?: (value: IDynamicFilterData) => IDynamicFilterData;
  searchFilters: (
    formData: IDynamicFilterData & { extraType?: string },
  ) => void;
  setFilters: (value: IDynamicFilterData) => void;
  isInputDisabled?: boolean;
  hideInput?: boolean;
  handleExport?: () => void;
  handleClearSearch?: () => void;
  placeholder?: string;
  isDateRequired?: boolean;
  isComissionamento?: boolean;
}

export const DynamicFilter = React.forwardRef<
  HTMLDivElement,
  DynamicFilterProps
>(
  (
    {
      filterSelectOptions,
      calendar = false,
      extraFilterSelectOptions,
      isLoading,
      filters,
      searchFilters,
      dataTreatment,
      handleExport,
      setFilters,
      isInputDisabled = false,
      hideInput = false,
      handleClearSearch,
      placeholder = 'Digite sua busca...',
      isDateRequired = false,
      isComissionamento = false,
    },
    ref,
  ) => {
    const [customPlaceholder, setCustomPlaceholder] =
      useState<string>(placeholder);

    const methods = useForm<IDynamicFilterData>({
      defaultValues: filters,
    });
    const { setValue, control, getValues, handleSubmit, watch } = methods;

    const termValue = watch('term');
    const typeValue = watch('type');

    useEffect(() => {
      if ((typeValue === 'cnpj' || typeValue === 'cpfCnpj') && termValue) {
        const uppercased = termValue.toUpperCase();
        if (termValue !== uppercased) {
          setValue('term', uppercased);
        }
      }
    }, [termValue, typeValue, setValue]);

    const handleChange = (newFilters: IDynamicFilterData) => {
      if (dataTreatment) {
        const treatedFilters = dataTreatment(newFilters);
        setValue('term', treatedFilters.term);
        setValue('type', treatedFilters.type);
        setValue('dateStart', treatedFilters.dateStart);
        setValue('extraType', treatedFilters.extraType);
        setValue('dateEnd', treatedFilters.dateEnd);
      } else {
        setValue('term', newFilters.term);
        setValue('type', newFilters.type);
        setValue('extraType', newFilters.extraType);
        setValue('dateStart', newFilters.dateStart);
        setValue('dateEnd', newFilters.dateEnd);
      }
    };

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedOption = filterSelectOptions.find(
        option => option.value === e.target.value,
      );
      const newPlaceholder = selectedOption?.placeholder || placeholder;

      setValue('type', e.target.value);
      setValue('term', '');

      const today = new Date();

      if (!isComissionamento) {
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(today.getFullYear() - 1);

        setValue('dateStart', oneYearAgo.toISOString().split('T')[0]);
        setValue('dateEnd', today.toISOString().split('T')[0]);
      }
      setCustomPlaceholder(newPlaceholder);
    };

    const onSubmit = (data: IDynamicFilterData) => {
      const cleanedData = {
        ...data,
        term: cleanTerm(data.term, data.type),
      };
      searchFilters(cleanedData);
      setFilters(cleanedData);
    };

    const cleanTerm = (term: string, type: string): string => {
      if (!term) return '';
      const numericTypes = [
        'cpf',
        'numeroPedido',
        'numPedAllied',
        'numNota',
        'nfe',
        'notaFiscal',
        'telefone',
        'cep',
      ];
      if (numericTypes.includes(type)) {
        return term.replace(/\D/g, '');
      }
      if (type === 'cnpj' || type === 'cpfCnpj') {
        return term.replace(/[./-]/g, '').toUpperCase();
      }
      return term;
    };

    const masksList = useCallback((value: string) => {
      return {
        cpf: masks.cpf,
        cnpj: masks.cnpj,
        numPedAllied: masks.numPedAllied,
        nfe: masks.numNota,
        numNota: masks.numNota,
        numeroPedido: masks.numPedido,
        notaFiscal: masks.numNota,
        cpfCnpj:
          /[A-Za-z]/.test(value) || value.replace(/[./-]/g, '').length > 11
            ? masks.cnpj
            : masks.cpfForCnpj,
      };
    }, []);

    return (
      <FormProvider {...methods}>
        <DF.Search onSubmit={handleSubmit(onSubmit)}>
          <DF.InputContainer>
            <DF.Stack $selectOnly={hideInput}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <DF.Select {...field} onChange={e => handleSelectChange(e)}>
                    {filterSelectOptions.map((option, index) => (
                      <DF.Option key={index} value={option.value}>
                        {option.description}
                      </DF.Option>
                    ))}
                  </DF.Select>
                )}
              />
              {!hideInput && (
                <Controller
                  name="term"
                  control={control}
                  render={({ field }) => {
                    const selectedOption = filterSelectOptions.find(
                      option => option.value === getValues('type'),
                    );
                    const mask =
                      masksList(field.value)[
                        selectedOption?.value as keyof typeof masksList
                      ] || '';

                    return (
                      <Input
                        {...field}
                        maxLength={35}
                        variant="dynamic-filter"
                        mask={mask ?? ''}
                        onKeyUp={e => {
                          if (e.key === 'Enter') {
                            handleSubmit(onSubmit)();
                          } else if (
                            e.key === 'Backspace' &&
                            field.value === ''
                          ) {
                            e.preventDefault();
                            handleClearSearch && handleClearSearch();
                          }
                        }}
                        onPaste={e => {
                          if (getValues('type') !== 'cpfCnpj') return;
                          e.preventDefault();
                          const text = e.clipboardData.getData('text');
                          const clean = text
                            .replace(/[./-]/g, '')
                            .toUpperCase();
                          let formatted: string;
                          if (/[A-Za-z]/.test(clean) || clean.length > 11) {
                            formatted = formatCNPJ(clean);
                          } else {
                            formatted = formatCPF(onlyNumbers(clean));
                          }
                          field.onChange(formatted);
                        }}
                        placeholder={customPlaceholder}
                        disabled={isInputDisabled}
                      />
                    );
                  }}
                />
              )}
            </DF.Stack>
          </DF.InputContainer>
          {calendar && (
            <DF.Stack>
              {window.location.pathname.includes('/comissionamento') && (
                <label
                  style={{
                    display: 'inline-block',
                    margin: 'auto 4px auto 4px',
                    minWidth: '130px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontWeight: '600',
                    fontSize: 13,
                  }}
                >
                  Data de Emissão da Nota de Vendas
                </label>
              )}
              <DF.Box>
                <Controller
                  name="dateStart"
                  control={control}
                  rules={{ required: isDateRequired }}
                  render={({ field }) => <InputDate {...field} />}
                />
                <DF.SeparatorHorizontal />
                <Controller
                  name="dateEnd"
                  control={control}
                  rules={{ required: isDateRequired }}
                  render={({ field }) => <InputDate {...field} />}
                />
              </DF.Box>
            </DF.Stack>
          )}

          {extraFilterSelectOptions && (
            <Controller
              name="extraType"
              control={control}
              render={({ field }) => (
                <DF.Select
                  {...field}
                  className={'highlighted'}
                  onChange={e => {
                    handleChange({ ...getValues(), extraType: e.target.value });
                  }}
                >
                  {extraFilterSelectOptions.map((option, index) => (
                    <DF.Option key={index} value={option.value}>
                      {option.description}
                    </DF.Option>
                  ))}
                </DF.Select>
              )}
            />
          )}

          <DF.ButtonsContainer>
            <DF.Button type="submit">Buscar</DF.Button>
            {handleExport && (
              <DF.Button
                onClick={handleExport}
                disabled={isLoading}
                className={'excel ' + (isLoading && 'is-disabled')}
                type="button"
              >
                <DF.ExcelDownload src={DownloadExcel} />
                Excel
              </DF.Button>
            )}
          </DF.ButtonsContainer>
        </DF.Search>
      </FormProvider>
    );
  },
);

DynamicFilter.displayName = 'DynamicFilter';
